import { useState, useMemo, useEffect, useCallback } from 'react';

import AppIcon from './AppIcon';
import { ROLES_AND_PERMISSIONS_LINK } from '@/components/Billing/billing-service.const';
import { FeatureTypes } from '@/components/Billing/billing-types';
import { AppSelect } from '@/react_app/components/Atoms';
import RoleSelectorItem from '@/react_app/components/RoleSelector/RoleSelectorItem';
import { ACCOUNT_ROLES, ACCOUNT_ROLES_BILLING_FEATURE, ACCOUNT_ROLES_DESCRIPTIONS, AccountRoles } from '@/utils/account-service.const';

const DIVIDER_INDEX = 3;
const POP_UP_MAX_WIDTH = 590;
// @ts-ignore
const entries: [AccountRoles, string][] = Object.entries(ACCOUNT_ROLES);

const RoleSelector = ({
	value,
	maxWidth,
	onChange,
	openUpgrade,
	disabledRoles,
	recommendedRole,
	isUpgradeRequired,
}: {
	maxWidth?: string;
	value?: AccountRoles;
	recommendedRole?: AccountRoles;
	disabledRoles?: AccountRoles[];
	onChange?: (role: AccountRoles) => void;
	isUpgradeRequired: (billingFeatureId: FeatureTypes) => boolean;
	openUpgrade: (e: unknown, billingFeatureId: FeatureTypes) => void;
}) => {
	const selectOptions = useMemo(
		() =>
			entries.toReversed().map(([value, label]) => {
				const billingFeatureId = ACCOUNT_ROLES_BILLING_FEATURE[value];
				return {
					value,
					label,
					billingFeatureId,
					recommended: value === recommendedRole,
					disabled: disabledRoles?.includes(value),
					description: ACCOUNT_ROLES_DESCRIPTIONS[value],
					isUpgradeRequired: billingFeatureId ? isUpgradeRequired(billingFeatureId) : false,
				};
			}),
		[entries, recommendedRole]
	);
	const topSectionOptions = useMemo(() => selectOptions.slice(0, DIVIDER_INDEX), [selectOptions]);
	const bottomSectionOptions = useMemo(() => selectOptions.slice(DIVIDER_INDEX), [selectOptions]);
	const options = useMemo(
		() => [
			{
				label: (
					<div className="px-3 flex w-full justify-between">
						<div className="text-[11px] text-[#8B969D] font-[500]">CHOOSE A WORKSPACE ROLE</div>
						<a target="_blank" href={ROLES_AND_PERMISSIONS_LINK}>
							<AppIcon name="info" />
						</a>
					</div>
				),
				options: topSectionOptions,
			},
			{
				label: (
					<div className="h-[32px] flex w-full items-center justify-center">
						<div className="w-full min-h-[1px] bg-[#D9D9D9] items-center justify-center" />
					</div>
				),
				options: bottomSectionOptions,
			},
		],
		[topSectionOptions, bottomSectionOptions]
	);

	const [selectValue, setSelectValue] = useState(value || selectOptions[0].value);

	const onSelectChange = useCallback(
		(role: AccountRoles) => {
			if (disabledRoles?.includes(role)) {
				return;
			}
			const optionToSelect = selectOptions.find((option) => option.value === role);
			if (optionToSelect?.isUpgradeRequired && optionToSelect.billingFeatureId) {
				return openUpgrade(null, optionToSelect.billingFeatureId);
			}
			setSelectValue(role);
			onChange?.(role);
		},
		[onChange, setSelectValue]
	);
	useEffect(() => {
		const selectedOption = selectOptions.find((option) => option.value === selectValue);
		if (selectedOption && (selectedOption.disabled || selectedOption.isUpgradeRequired)) {
			const firstEnabledOption = selectOptions.find((option) => !option.disabled && !option.isUpgradeRequired);
			if (firstEnabledOption) {
				onSelectChange(firstEnabledOption.value);
			}
		}
	}, [selectValue, onSelectChange]);

	useEffect(() => {
		if (value && value !== selectValue) {
			setSelectValue(value);
		}
	}, [value]);

	return (
		<AppSelect
			listHeight={440}
			options={options}
			value={selectValue}
			onSelect={onSelectChange}
			placeholder="Select a role"
			className="w-full text-left"
			popupMatchSelectWidth={POP_UP_MAX_WIDTH}
			style={{ maxWidth: maxWidth || `${POP_UP_MAX_WIDTH}px` }}
			optionRender={(option) => (
				<RoleSelectorItem
					label={option.data.label}
					disabled={option.data.disabled}
					description={option.data.description}
					recommended={option.data.recommended}
					billingFeatureId={option.data.billingFeatureId}
					isUpgradeRequired={option.data.isUpgradeRequired}
				/>
			)}
		/>
	);
};

export default RoleSelector;
